// source-hash — compute a stable 8-char hash of a CourseDraft's "input shape".
// Per LLM_NATIVE_ARCHITECTURE.md §3 stale state · §6.2 hash convention.
//
// What goes into the hash:
//   - draft.sources[].sha256 (sorted)        — file content fingerprint
//   - draft.discipline + customDiscipline?
//   - draft.examType?                         — 19BO+ P2-1: changes rule subset
//   - draft.pedagogyTypes (sorted) + customPedagogy?
//   - draft.contentLanguage
//   - draft.masteryLevel
//   - draft.examFormat (canonical-stringified)
//   - draft.mockPapers[].id+style+pastPaperRef? (sorted by id)
//
// What does NOT go into the hash:
//   - draft.id / title / examDate — cosmetic; changing these doesn't
//     invalidate generated content
//   - draft.createdAt / updatedAt — clock noise
//   - draft.enabledComponents — affects ingest scope but not source meaning;
//     a user toggling 'mindmap' off shouldn't mark old artefacts stale
//
// Mismatch policy (consumer side · course-context.tsx):
//   pkg.sourceHash === currentDraftHash → fresh
//   pkg.sourceHash !== undefined && !== current → STALE → setIsStale(true)
//   pkg.sourceHash === undefined → legacy artefact → not stale (don't pester)
//
// Hash function reuses FNV-1a 8-hex from the prompt-version pattern — sync,
// no deps, identification-grade (collision probability negligible at this
// scale). See packages/ingest/src/llm/prompt-version.ts for the same logic;
// re-implemented here to keep web ↛ ingest dependency direction.

import type { CourseDraft } from '@examforge/shared/schemas';

/** FNV-1a 32-bit, 8 hex chars. Sync, no deps. */
function fnv1a(text: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

/** Canonical JSON: sort object keys recursively for stable hashing. Arrays
 *  keep their order (caller responsible for sorting if needed). */
function canonicalJson(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) {
    return `[${value.map(canonicalJson).join(',')}]`;
  }
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  const entries = keys.map((k) => `${JSON.stringify(k)}:${canonicalJson(obj[k])}`);
  return `{${entries.join(',')}}`;
}

/** Compute the source-hash for the given draft. */
export function computeSourceHash(draft: CourseDraft): string {
  const sources = (draft.sources ?? [])
    .map((s) => s.sha256)
    .filter((h): h is string => typeof h === 'string')
    .sort();

  const pedagogy = [...draft.pedagogyTypes].sort();

  const mockPapers = (draft.mockPapers ?? [])
    .map((mp) => ({
      id: mp.id,
      style: mp.style,
      pastPaperRef: mp.pastPaperRef ?? null,
    }))
    .sort((a, b) => a.id.localeCompare(b.id));

  const payload = {
    sources,
    discipline: draft.discipline,
    customDiscipline: draft.customDiscipline ?? null,
    // 19BO+ P2-1: examType changes which discipline rules are injected → must
    // invalidate stale artefacts when the user switches exam scenario.
    examType: draft.examType ?? null,
    pedagogy,
    customPedagogy: draft.customPedagogy ?? null,
    contentLanguage: draft.contentLanguage,
    masteryLevel: draft.masteryLevel,
    examFormat: draft.examFormat,
    mockPapers,
  };

  return fnv1a(canonicalJson(payload));
}

/** Stale-check helper. See header for policy. */
export function isPackageStale(
  pkgSourceHash: string | undefined,
  currentDraftHash: string,
): boolean {
  if (!pkgSourceHash) return false;        // legacy artefact, not pester-worthy
  return pkgSourceHash !== currentDraftHash;
}
